
import { mdiClose, mdiHeartOutline } from '@mdi/js'
import Icon from '@mdi/react'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import { getQuotes } from 'admin/services/quotes/actions'
import AnyCard from 'components/Card'
import { ContentCardAction } from 'components/Card/ContentCardActions'
import CollapsibleTextInput from 'components/CollapsibleTextInput'
import EmptyView from 'components/EmptyView'
import ScrollListener from 'components/ScrollListener'
import { ContentItem, STATUS_TYPE } from 'interfaces'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { renderLoadingCards } from 'routes/find/routes/home/components/utils'
import { of as observableOf, Subject, zip } from 'rxjs'
import { catchError, switchMap, tap } from 'rxjs/operators'
import { getFavoriteStatusIdeas } from 'services/content/favorites'
import { addedFavoritesSelectorForType } from 'services/content/selectors'
import { searchStatusIdeas } from 'services/content/statuses/actions'
import { message } from 'services/snackbar'
import StoreState, { StoreThunkDispatch } from 'store/state'
import { CHIP_ACTIVE_CLASS } from 'theme/theme-md'
import FetchRandomContent from './FetchRandomContent'
import styles from './TextContentSearchPanel.pcss'

const SCROLL_EMIT_TRESHOLD = 250
const TEXT_FAVORITES_TYPE = 'text-favorites'
const QUOTES_TYPE = 'quotes'

type PanelContentType = typeof TEXT_FAVORITES_TYPE
  | typeof QUOTES_TYPE
  | typeof STATUS_TYPE

const TEXT_TYPE_FILTERS: PanelContentType[] = [STATUS_TYPE, QUOTES_TYPE, TEXT_FAVORITES_TYPE]

const CARD_ACTIONS: ContentCardAction[] = ['create-post', 'open-studio']

const initialResults: Readonly<Record<string, ContentItem[]>> = {
  [QUOTES_TYPE]: [],
  [STATUS_TYPE]: []
}

interface TextContentSearchPanelProps {
  scrollElement?: HTMLElement | null
}

export function TextContentSearchPanel(props: TextContentSearchPanelProps) {
  const intl = useIntl()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const [activeType, setActiveType] = useState<PanelContentType>(STATUS_TYPE)
  const [query, setQuery] = useState('')
  const [items, setItems] = useState<Record<string, ContentItem[]>>(initialResults)
  const [favTexts, setFavTexts] = useState<ContentItem[]>([])
  const [loading, setLoading] = useState(true)
  const [favLoading, setFavLoading] = useState(false)
  const addedFavTexts = useSelector((state: StoreState) => addedFavoritesSelectorForType(state, STATUS_TYPE))
  const search$ = useRef<Subject<{ query: string }> | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [hasText, setHasText] = useState(false)
  const page$ = useRef(0)
  // EXPL: highest page that actually came back. The next page is derived from it rather than
  // from `page$`, so a failed fetch is retried instead of skipped - including page 0.
  const loadedPage$ = useRef(-1)
  const [randomQuotes, setRandomQuotes] = useState<ContentItem[]>([])
  const [randomStatuses, setRandomStatuses] = useState<ContentItem[]>([])

  useEffect(() => {
    const fetchFavorites = async () => {
      setFavLoading(true)
      try {
        const response = await dispatch(getFavoriteStatusIdeas(0)).unwrap()
        setFavTexts(response as any)
      } catch (e) {
        console.log(`Error fetching favorites: ${e}`)
      }
      setFavLoading(false)
    }
    fetchFavorites()
  }, [dispatch])

  useEffect(() => {
    const subject = new Subject<{ query: string }>()
    search$.current = subject
    const sub = subject
      .pipe(
        // EXPL: an empty query is the cancel signal (see `clearSearch`) - switchMap drops the
        // in-flight request and nothing is loading afterwards
        tap(({ query }) => setLoading(Boolean(query))),
        switchMap(({ query }) => {
          if (!query) {
            return observableOf({ cancelled: true })
          }
          return zip(
            dispatch(getQuotes(page$.current, query)),
            dispatch(searchStatusIdeas(query, page$.current))
          )
            .pipe(
              catchError(() => {
                dispatch(message(intl.formatMessage({ id: 'errors.generic' }), 'error'))
                return observableOf({ error: true })
              })
            )
        })
      )
      .subscribe((response: any) => {
        if (response?.cancelled) {
          return
        }
        if (!response.error) {
          const [quotes, statuses] = response
          loadedPage$.current = page$.current
          setItems(current => ({
            [QUOTES_TYPE]: current[QUOTES_TYPE].concat(quotes),
            [STATUS_TYPE]: current[STATUS_TYPE].concat(statuses.data)
          }))
        }
        // NOTE: on failure `loadedPage$` is left alone, so the next scroll retries the same page
        setLoading(false)
      })

    return () => {
      // EXPL: `Subject.unsubscribe()` only closes the subject - it does not tear the subscriber
      // chain down, so the switchMap's in-flight request kept running and set state after
      // unmount. The Subscription has to be unsubscribed as well.
      sub.unsubscribe()
      subject.complete()
      search$.current = null
    }
  }, [dispatch, intl])

  const search = useCallback((value: string) => {
    const q = value.trim()
    if (q) {
      setQuery(q)
      setLoading(true)
      setItems(initialResults)
      page$.current = 0
      loadedPage$.current = -1
      search$.current?.next({ query: q })
    }
  }, [])

  useEffect(() => {
    const el = inputRef.current
    if (!el) {
      return
    }
    const onInput = () => setHasText(Boolean(el.value))
    el.addEventListener('input', onInput)
    return () => el.removeEventListener('input', onInput)
  }, [])

  const clearSearch = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = ''
    }
    setHasText(false)
    setQuery('')
    setItems(initialResults)
    setLoading(false)
    page$.current = 0
    loadedPage$.current = -1
    // EXPL: cancels the in-flight request. Without it the pending response lands after the
    // reset and refills the list the user just cleared.
    search$.current?.next({ query: '' })
    inputRef.current?.focus()
  }, [])

  const onLoadNextPage = useCallback(() => {
    const q = query.trim()
    if (loading || !q) {
      return
    }
    page$.current = loadedPage$.current + 1
    search$.current?.next({ query: q })
  }, [query, loading])

  const onRandomContentFetched = useCallback((type: 'quotes' | 'statuses', items: ContentItem[]) => {
    setLoading(false)
    switch (type) {
      case 'quotes':
        setRandomQuotes(items)
        break
      case 'statuses':
        setRandomStatuses(items)
    }
  }, [])

  const visibleItems = useMemo(() => {
    if (activeType === TEXT_FAVORITES_TYPE) {
      const byId = new Map<string, ContentItem>()
      favTexts.forEach(item => byId.set(item.id, item))
      Object.values(addedFavTexts).forEach(item => byId.set(item.id, item))
      return Array.from(byId.values())
    }
    return items[activeType]
  }, [activeType, favTexts, items, addedFavTexts])

  const randomVisibleItems = useMemo(() => {
    switch (activeType) {
      case QUOTES_TYPE:
        return randomQuotes
      case STATUS_TYPE:
        return randomStatuses
      default:
        return []
    }
  }, [activeType, randomQuotes, randomStatuses])

  const loadingCards = useMemo(() => {
    if (!favLoading && (activeType === TEXT_FAVORITES_TYPE)) {
      return null
    }
    if (!loading) {
      return null
    }

    return renderLoadingCards(undefined, false, true, loading)
  }, [favLoading, activeType, loading])

  const isFavoritesView = activeType === TEXT_FAVORITES_TYPE
  const isEmpty = visibleItems.length === 0

  return (
    <Fragment>
      <div
        className={styles.container}
        data-testid="text-content-search-panel"
      >
        <div className={styles.top}>
          <div className={styles.nav}>
            {TEXT_TYPE_FILTERS.map(type => (
              <NavChip key={type} value={type} isActive={activeType === type} onClick={setActiveType} />
            ))}
          </div>
          <CollapsibleTextInput
            ref={inputRef}
            initialActive
            changeOnEnter
            disabled={isFavoritesView}
            placeholder={intl.formatMessage({
              id: activeType === 'quotes' ? 'find.quotes.search-hint' : 'find.status-ideas.search-hint'
            })}
            className={styles['search-box']}
            activeClassName={styles['search-active']}
            id="text-content-search-input"
            onValueChanged={search}
            actionButton={hasText ? (
              <IconButton size="small" aria-label="Clear search" className={styles['clear-btn']} onClick={clearSearch}>
                <Icon path={mdiClose} size="16px" className={styles['icon-clear']} />
              </IconButton>
            ) : undefined}
          />
        </div>
        <div className={styles.content}>
          <ScrollListener
            onScroll={onLoadNextPage}
            emitTreshold={SCROLL_EMIT_TRESHOLD}
            key={activeType + query}
            scrollElement={props.scrollElement ?? undefined}
          >
            <div className={styles.grid}>
              {visibleItems.map(item => (
                <AnyCard
                  key={item.id}
                  content={item}
                  children={item}
                  square
                  small
                  hideDetails
                  actions={CARD_ACTIONS}
                />
              ))}
              {isEmpty && !loading && !isFavoritesView && randomVisibleItems.map(item => (
                <AnyCard
                  key={item.id}
                  content={item}
                  children={item}
                  square
                  small
                  hideDetails
                  actions={CARD_ACTIONS}
                />
              ))}
              {loadingCards}
            </div>
          </ScrollListener>
          {isFavoritesView && !favLoading && isEmpty && (
            <EmptyView
              title="No texts favorited yet"
              icon={mdiHeartOutline}
              subtitle={(
                <div>
                  Click ♡ on any content to save it to your favorites.
                </div>
              )}
              carousel
              className={styles.error}
            />
          )}
          {!isFavoritesView && !loading && isEmpty && Boolean(query) && (
            <EmptyView
              carousel
              title="No texts available for that search"
              subtitle="Please try a different keyword, topic, brand or industry."
              className={styles.error}
            />
          )}
        </div>
      </div>
      <FetchRandomContent onFetched={onRandomContentFetched as any} />
    </Fragment>
  )
}

function NavChip(props: { value: PanelContentType, isActive: boolean, onClick: (value: PanelContentType) => void }) {
  const onClick = () => {
    props.onClick(props.value)
  }

  return (
    <Chip
      label={<FormattedMessage id={`general.content.filters.${props.value}`} />}
      className={`${styles.chip} ${props.isActive ? CHIP_ACTIVE_CLASS : ''}`}
      onClick={onClick}
    />
  )
}

export default TextContentSearchPanel
